/**
 * Styles Builder Renderer
 *
 * This module provides a unified rendering pipeline for the Styles Builder.
 * It generates both the preview HTML (shown in iframe) and the prompt examples
 * (sent to AI) from the same source, ensuring consistency.
 *
 * Key principle: What the user sees IS what the AI receives as reference.
 */

import {
  type RenderedComponent,
  renderComponents,
  renderComponentsHTML,
} from "@/components/ProjectLanding/StylesBuilder/componentRegistry";
import type { DesignTokens, StyleSelections } from "./styleResolver";
import {
  resolveTokens,
  tokensToAnimationCSS,
  tokensToCSSVariables,
} from "./styleResolver";

export interface RenderOutput {
  /** The full HTML document for iframe preview */
  fullHTML: string;
  /** Individual rendered components with metadata */
  components: RenderedComponent[];
  /** CSS variables as a string */
  cssVariables: string;
  /** Animation CSS rules */
  animationCSS: string;
  /** The resolved design tokens */
  tokens: DesignTokens;
}

export interface PromptOutput {
  /** Text description for system prompt (visible to user) */
  systemPromptText: string;
  /** HTML examples for additional instructions (hidden from user) */
  additionalInstructions: string;
}

const BASE_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Bricolage+Grotesque:wght@400;500;600;700&family=IBM+Plex+Sans:wght@400;500;600;700&family=Source+Sans+3:wght@400;500;600;700&family=Playfair+Display:wght@400;500;600;700&family=Crimson+Pro:wght@400;500;600;700&family=Newsreader:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600;700&family=Fira+Code:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap');

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* Dark scrollbar to match Tempo app */
  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  ::-webkit-scrollbar-corner {
    background: transparent;
  }

  /* Firefox */
  * {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
  }

  body {
    font-family: var(--font);
    background: var(--bg);
    color: var(--text);
    font-size: 11px;
    line-height: 1.5;
    padding: 24px;
    min-height: 100vh;
    position: relative;
    overflow-x: hidden;
  }

  /* =========================================================================
     STYLE-SPECIFIC ATMOSPHERIC BACKGROUNDS
     Each style has a unique visual atmosphere
     ========================================================================= */

  /* MINIMAL / BRUTALIST: Subtle noise texture for depth */
  body.minimal-style::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
    opacity: 0.03;
    z-index: -1;
    pointer-events: none;
  }

  /* BOLD / NEON NOIR: Dramatic gradient glow */
  body.bold-style::before {
    content: '';
    position: fixed;
    inset: 0;
    background:
      radial-gradient(ellipse at 0% 0%, rgba(139, 92, 246, 0.15) 0%, transparent 50%),
      radial-gradient(ellipse at 100% 100%, rgba(236, 72, 153, 0.12) 0%, transparent 50%),
      radial-gradient(ellipse at 50% 50%, rgba(59, 130, 246, 0.08) 0%, transparent 70%);
    z-index: -1;
    pointer-events: none;
  }

  /* SOFT / DREAMSCAPE: Warm, atmospheric gradient */
  body.soft-style::before {
    content: '';
    position: fixed;
    inset: 0;
    background:
      radial-gradient(ellipse at 30% 20%, rgba(167, 139, 250, 0.2) 0%, transparent 50%),
      radial-gradient(ellipse at 70% 80%, rgba(251, 146, 60, 0.1) 0%, transparent 50%),
      radial-gradient(ellipse at 50% 50%, rgba(236, 72, 153, 0.08) 0%, transparent 60%);
    z-index: -1;
    pointer-events: none;
  }

  /* ELEGANT / MIDNIGHT LUXE: Subtle gold shimmer */
  body.elegant-style::before {
    content: '';
    position: fixed;
    inset: 0;
    background:
      radial-gradient(ellipse at 80% 20%, rgba(217, 180, 115, 0.12) 0%, transparent 40%),
      radial-gradient(ellipse at 20% 80%, rgba(180, 140, 90, 0.08) 0%, transparent 40%);
    z-index: -1;
    pointer-events: none;
  }

  /* CORPORATE / TECH SLATE: Precise geometric accent */
  body.corporate-style::before {
    content: '';
    position: fixed;
    inset: 0;
    background:
      linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, transparent 50%),
      linear-gradient(315deg, rgba(99, 102, 241, 0.03) 0%, transparent 50%);
    z-index: -1;
    pointer-events: none;
  }

  /* GLASS / AURORA: Multi-color ethereal glow for blur effect */
  body.glass-style::before {
    content: '';
    position: fixed;
    inset: 0;
    background:
      radial-gradient(ellipse at 20% 30%, rgba(139, 92, 246, 0.35) 0%, transparent 45%),
      radial-gradient(ellipse at 80% 70%, rgba(59, 130, 246, 0.3) 0%, transparent 45%),
      radial-gradient(ellipse at 50% 50%, rgba(236, 72, 153, 0.25) 0%, transparent 55%),
      radial-gradient(ellipse at 30% 80%, rgba(34, 211, 238, 0.2) 0%, transparent 40%);
    z-index: -1;
    pointer-events: none;
    animation: aurora 15s ease-in-out infinite alternate;
  }

  @keyframes aurora {
    0% { opacity: 1; transform: scale(1); }
    100% { opacity: 0.8; transform: scale(1.1); }
  }

  /* Preview Grid - Masonry Layout with generous spacing */
  .preview-grid {
    column-count: 2;
    column-gap: 28px;
  }

  .section {
    display: flex;
    flex-direction: column;
    gap: 10px;
    break-inside: avoid;
    margin-bottom: 28px;
  }

  .section-label {
    font-size: 9px;
    font-weight: 600;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.8px;
  }

  /* Card */
  .card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
    box-shadow: var(--shadow);
  }

  .card.glass {
    background: var(--glass-bg);
    border-color: var(--glass-border);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
  }

  .card-image {
    height: 70px;
    background: linear-gradient(135deg, var(--bg-muted) 0%, var(--bg-hover) 100%);
  }

  .card-content {
    padding: 16px;
  }

  .card-title {
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 6px;
    color: var(--text);
  }

  .card-desc {
    font-size: 11px;
    color: var(--text-muted);
    margin-bottom: 14px;
    line-height: 1.5;
  }

  .card-actions {
    display: flex;
    gap: 10px;
  }

  /* Form */
  .form-header {
    padding: 14px 16px;
    border-bottom: 1px solid var(--border);
  }

  .form-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--text);
  }

  .form-desc {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 4px;
  }

  .form-body {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .label {
    font-size: 11px;
    font-weight: 500;
    color: var(--text);
  }

  .input {
    height: 32px;
    padding: 0 12px;
    background: var(--input-bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    font-size: 11px;
    color: var(--text);
    outline: none;
  }

  .input::placeholder {
    color: var(--text-muted);
  }

  .input:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 15%, transparent);
  }

  .textarea {
    height: 70px;
    padding: 10px 12px;
    resize: vertical;
  }

  .select {
    height: 32px;
    padding: 0 12px;
    background: var(--input-bg);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    font-size: 11px;
    color: var(--text-muted);
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .form-actions {
    margin-top: 6px;
  }

  .inputs-showcase {
    padding: var(--space-3);
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  /* Buttons */
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 30px;
    padding: 0 14px;
    font-size: 11px;
    font-weight: 500;
    border-radius: var(--radius-sm);
    border: none;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-sm {
    height: 26px;
    padding: 0 10px;
    font-size: 10px;
  }

  .btn-lg {
    height: 36px;
    padding: 0 18px;
    font-size: 12px;
  }

  .btn-primary {
    background: var(--accent);
    color: var(--accent-text);
  }

  .btn-primary:hover {
    background: var(--accent-hover);
    transform: translateY(-1px);
  }

  .btn-secondary {
    background: transparent;
    color: var(--text);
    border: 1px solid var(--border);
  }

  .btn-secondary:hover {
    background: var(--bg-muted);
    border-color: var(--text-muted);
  }

  .btn-ghost {
    background: transparent;
    color: var(--text);
    border: none;
  }

  .btn-ghost:hover {
    background: var(--bg-muted);
  }

  .btn-full {
    width: 100%;
  }

  .button-showcase {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    padding: var(--space-3);
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
  }

  /* Stats */
  .stats-grid {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .stat-card {
    padding: 14px 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .stat-label {
    font-size: 10px;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .stat-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--text);
    letter-spacing: -0.5px;
  }

  .stat-change {
    font-size: 10px;
    font-weight: 600;
  }

  .stat-change.positive {
    color: #10b981;
  }

  .stat-change.negative {
    color: #ef4444;
  }

  /* Settings */
  .settings-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    border-bottom: 1px solid var(--border);
  }

  .settings-row:last-child {
    border-bottom: none;
  }

  .settings-info {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .settings-label {
    font-size: 11px;
    font-weight: 500;
    color: var(--text);
  }

  .settings-desc {
    font-size: 10px;
    color: var(--text-muted);
  }

  .toggle {
    width: 36px;
    height: 20px;
    background: var(--bg-muted);
    border-radius: 12px;
    position: relative;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .toggle.active {
    background: var(--accent);
  }

  .toggle-dot {
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 50%;
    position: absolute;
    top: 2px;
    left: 2px;
    transition: transform 0.2s ease;
  }

  .toggle.active .toggle-dot {
    transform: translateX(16px);
  }

  /* Table */
  .table-container {
    overflow: hidden;
  }

  .table {
    width: 100%;
    border-collapse: collapse;
    font-size: 10px;
  }

  .table th,
  .table td {
    padding: var(--space-2) var(--space-3);
    text-align: left;
    border-bottom: 1px solid var(--border);
  }

  .table th {
    font-weight: 500;
    color: var(--text-muted);
    background: var(--bg-muted);
  }

  .table td {
    color: var(--text);
  }

  .table tr:last-child td {
    border-bottom: none;
  }

  /* Badges */
  .badge {
    display: inline-flex;
    align-items: center;
    padding: 2px 6px;
    font-size: 9px;
    font-weight: 500;
    border-radius: var(--radius-sm);
  }

  .badge-success {
    background: rgba(34, 197, 94, 0.1);
    color: #22c55e;
  }

  .badge-warning {
    background: rgba(234, 179, 8, 0.1);
    color: #eab308;
  }

  .badge-neutral {
    background: var(--bg-muted);
    color: var(--text-muted);
  }

  /* Navigation */
  .nav-showcase {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
  }

  .navbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--space-2) var(--space-3);
  }

  .nav-brand {
    font-weight: 600;
    font-size: 12px;
    color: var(--text);
  }

  .nav-links {
    display: flex;
    gap: var(--space-3);
  }

  .nav-link {
    font-size: 10px;
    color: var(--text-muted);
    text-decoration: none;
    cursor: pointer;
  }

  .nav-link:hover,
  .nav-link.active {
    color: var(--text);
  }

  /* Hero */
  .section-wide {
    break-inside: avoid;
    column-span: all;
  }

  .hero {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 28px;
    text-align: center;
    box-shadow: var(--shadow);
  }

  .glass .hero,
  body.glass-style .hero {
    background: var(--glass-bg);
    border-color: var(--glass-border);
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
  }

  .hero-badge {
    display: inline-block;
    padding: 4px 10px;
    background: color-mix(in srgb, var(--accent) 15%, transparent);
    color: var(--accent);
    font-size: 9px;
    font-weight: 600;
    border-radius: 999px;
    margin-bottom: 14px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .hero-title {
    font-size: 22px;
    font-weight: 700;
    color: var(--text);
    margin-bottom: 10px;
    line-height: 1.2;
    letter-spacing: -0.5px;
  }

  .hero-desc {
    font-size: 12px;
    color: var(--text-muted);
    margin-bottom: 20px;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
  }

  .hero-actions {
    display: flex;
    gap: 10px;
    justify-content: center;
  }

  /* Pricing */
  .pricing-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .pricing-card {
    padding: 18px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: relative;
  }

  .pricing-featured {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent), var(--shadow);
  }

  .pricing-featured .badge {
    position: absolute;
    top: -8px;
    right: 12px;
  }

  .pricing-tier {
    font-size: 12px;
    font-weight: 600;
    color: var(--text);
  }

  .pricing-price {
    display: flex;
    align-items: baseline;
    gap: 2px;
  }

  .pricing-amount {
    font-size: 26px;
    font-weight: 700;
    color: var(--text);
    letter-spacing: -1px;
  }

  .pricing-period {
    font-size: 11px;
    color: var(--text-muted);
  }

  .pricing-features {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
  }

  .pricing-features li {
    font-size: 10px;
    color: var(--text-muted);
    padding-left: 14px;
    position: relative;
  }

  .pricing-features li::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: var(--accent);
    font-size: 9px;
  }

  /* Testimonial */
  .testimonial {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .testimonial-quote {
    font-size: 12px;
    font-style: italic;
    color: var(--text);
    line-height: 1.6;
    position: relative;
    padding-left: 16px;
    border-left: 3px solid var(--accent);
  }

  .testimonial-author {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent) 0%, color-mix(in srgb, var(--accent) 70%, var(--text)) 100%);
  }

  .testimonial-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .testimonial-name {
    font-size: 11px;
    font-weight: 600;
    color: var(--text);
  }

  .testimonial-role {
    font-size: 10px;
    color: var(--text-muted);
  }

  /* Alerts */
  .alert-showcase {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .alert {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    border-radius: var(--radius-sm);
    font-size: 10px;
  }

  .alert-icon {
    flex-shrink: 0;
  }

  .alert-info {
    background: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
    border: 1px solid rgba(59, 130, 246, 0.2);
  }

  .alert-success {
    background: rgba(34, 197, 94, 0.1);
    color: #22c55e;
    border: 1px solid rgba(34, 197, 94, 0.2);
  }

  .alert-warning {
    background: rgba(234, 179, 8, 0.1);
    color: #eab308;
    border: 1px solid rgba(234, 179, 8, 0.2);
  }

  .alert-error {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.2);
  }

  /* Tabs */
  .tabs {
    display: flex;
    gap: 2px;
    padding: 4px;
    background: var(--bg-muted);
    border-radius: var(--radius-sm);
    margin: 12px;
    margin-bottom: 0;
  }

  .tab {
    flex: 1;
    padding: 8px 12px;
    font-size: 10px;
    font-weight: 500;
    color: var(--text-muted);
    background: transparent;
    border: none;
    border-radius: calc(var(--radius-sm) - 2px);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .tab:hover {
    color: var(--text);
  }

  .tab.active {
    background: var(--bg-card);
    color: var(--text);
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  }

  .tab-content {
    padding: 14px;
    font-size: 11px;
    color: var(--text-muted);
  }

  /* Progress */
  .progress-showcase {
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .progress-item {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .progress-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .progress-label {
    font-size: 10px;
    color: var(--text);
  }

  .progress-value {
    font-size: 10px;
    font-weight: 600;
    color: var(--text-muted);
  }

  .progress-bar {
    height: 6px;
    background: var(--bg-muted);
    border-radius: 3px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: var(--accent);
    border-radius: 3px;
    transition: width 0.3s ease;
  }

  /* Badges showcase */
  .badges-showcase {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .badge-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .badge-primary {
    background: var(--accent);
    color: var(--accent-text);
  }

  .badge-secondary {
    background: var(--bg-muted);
    color: var(--text);
  }

  .badge-error {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }

  .badge-outline-primary {
    background: transparent;
    border: 1px solid var(--accent);
    color: var(--accent);
  }

  .badge-outline-secondary {
    background: transparent;
    border: 1px solid var(--border);
    color: var(--text-muted);
  }

  .badge-outline-success {
    background: transparent;
    border: 1px solid #22c55e;
    color: #22c55e;
  }
`;

/**
 * Generates the full HTML template for preview.
 */
export function generateHTMLTemplate(
  tokens: DesignTokens,
  componentTypes?: string[],
): string {
  const cssVariables = tokensToCSSVariables(tokens);
  const animationCSS = tokensToAnimationCSS(tokens);
  const componentsHTML = renderComponentsHTML(tokens, componentTypes);

  const bodyClass = tokens.style ? `${tokens.style}-style` : "";

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <style>
        :root {
          ${cssVariables}
        }
        ${BASE_CSS}
        ${animationCSS}
      </style>
    </head>
    <body class="${bodyClass}">
      ${componentsHTML}
    </body>
    </html>
  `;
}

/**
 * Main render function that produces all outputs from selections.
 */
export function renderFromSelections(
  selections: StyleSelections,
  componentTypes?: string[],
): RenderOutput {
  const tokens = resolveTokens(selections);
  const cssVariables = tokensToCSSVariables(tokens);
  const animationCSS = tokensToAnimationCSS(tokens);
  const components = renderComponents(tokens, componentTypes);
  const fullHTML = generateHTMLTemplate(tokens, componentTypes);

  return {
    fullHTML,
    components,
    cssVariables,
    animationCSS,
    tokens,
  };
}

/**
 * Renders for a specific theme (used for "both" theme split view).
 */
export function renderForTheme(
  selections: StyleSelections,
  theme: "light" | "dark",
  componentTypes?: string[],
): RenderOutput {
  return renderFromSelections({ ...selections, theme }, componentTypes);
}

/**
 * Generates prompt content from selections.
 *
 * Returns both:
 * - systemPromptText: Text description (visible in user's prompt)
 * - additionalInstructions: HTML examples (hidden in additional instructions)
 */
export function generatePromptContent(
  selections: StyleSelections,
  componentTypes?: string[],
): PromptOutput {
  const output = renderFromSelections(selections, componentTypes);
  const { tokens, components } = output;

  const tokenDescriptions: string[] = [];

  if (tokens.theme) {
    const themeDesc =
      tokens.theme === "dark"
        ? `Dark theme with backgrounds (${tokens.colors.background}, ${tokens.colors.backgroundCard}), light text (${tokens.colors.text})`
        : `Light theme with backgrounds (${tokens.colors.background}, ${tokens.colors.backgroundCard}), dark text (${tokens.colors.text})`;
    tokenDescriptions.push(`- Theme: ${themeDesc}`);
  }

  tokenDescriptions.push(
    `- Accent color: ${tokens.colors.accent}. Use for primary buttons, links, active states, focus rings.`,
  );
  tokenDescriptions.push(
    `- Border radius: ${tokens.radius.md} (sm: ${tokens.radius.sm}, lg: ${tokens.radius.lg})`,
  );
  tokenDescriptions.push(`- Shadow: ${tokens.shadow.card}`);
  tokenDescriptions.push(`- Font: ${tokens.typography.fontFamily}`);
  tokenDescriptions.push(
    `- Spacing scale: ${Object.entries(tokens.spacing.scale)
      .slice(0, 4)
      .map(([k, v]) => `${k}=${v}`)
      .join(", ")}`,
  );

  if (tokens.animation.enabled) {
    tokenDescriptions.push(
      `- Animations: ${tokens.animation.duration} ${tokens.animation.easing} transitions`,
    );
  } else {
    tokenDescriptions.push(`- Animations: None (static UI)`);
  }

  const systemPromptText = `## UI/UX Requirements (User-Specified)
The following design specifications must be followed:
${tokenDescriptions.join("\n")}`;

  const examplesMarkdown = components
    .map(
      (comp) => `### ${comp.name} Component
\`\`\`html
${comp.html.trim()}
\`\`\``,
    )
    .join("\n\n");

  const additionalInstructions = `## Component Reference Examples

The user expects components to match these exact patterns.
Use these as reference for styling decisions:

${examplesMarkdown}

**Important:**
- Match the exact colors, spacing, border-radius, and shadow values shown
- Use CSS variables where applicable (--accent, --bg-card, --border, etc.)
- Extrapolate these patterns to other components you create
- Maintain visual consistency across all components`;

  return {
    systemPromptText,
    additionalInstructions,
  };
}
